import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Loader2, Copy, CheckCircle2, XCircle,
  ChevronDown, ChevronRight, FileText, AlertTriangle, Save,
} from 'lucide-react';
import ingestionService from '../api';

const STATUS_LABEL = {
  pending: { cls: 'bg-yellow-100 text-yellow-700', label: 'Pending Review' },
  approved: { cls: 'bg-green-100 text-green-700', label: 'Approved' },
  rejected: { cls: 'bg-red-100 text-red-700', label: 'Rejected' },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || { cls: 'bg-gray-100 text-gray-600', label: status || 'Unknown' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

function ClusterCard({ cluster, onReview }) {
  const [open, setOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [rationale, setRationale] = useState('');

  const handleReview = async (status) => {
    setReviewing(true);
    try {
      await onReview(cluster.cluster_id || cluster.id, { status, reviewed_by: 'user', rationale });
    } finally {
      setReviewing(false);
    }
  };

  const members = cluster.members || [];
  const canonical = cluster.canonical_filename || (members[0]?.filename);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <Copy className="w-5 h-5 text-orange-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{canonical || `Cluster #${cluster.cluster_id || cluster.id}`}</p>
          <p className="text-xs text-gray-400">{members.length} document{members.length !== 1 ? 's' : ''} in cluster</p>
        </div>
        <StatusBadge status={cluster.status} />
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-3">
          <div className="space-y-1.5">
            {members.map((m, i) => (
              <div key={m.id || i} className="flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                <span className="text-xs text-gray-700 flex-1 truncate">{m.filename || m.filepath}</span>
                {m.similarity != null && (
                  <span className="text-xs font-medium text-orange-600">{(m.similarity * 100).toFixed(1)}%</span>
                )}
                {m.id === cluster.canonical_doc_id && (
                  <span className="text-xs bg-blue-50 text-primaryDark px-1.5 py-0.5 rounded font-medium">canonical</span>
                )}
              </div>
            ))}
          </div>

          {(!cluster.status || cluster.status === 'pending') && (
            <div className="space-y-2 pt-1 border-t border-gray-100">
              <textarea
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                rows={2}
                placeholder="Optional: add a rationale for your decision…"
                value={rationale}
                onChange={e => setRationale(e.target.value)}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleReview('approved')}
                  disabled={reviewing}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {reviewing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                  Approve (keep canonical)
                </button>
                <button
                  onClick={() => handleReview('rejected')}
                  disabled={reviewing}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-md text-xs font-medium hover:bg-red-100 disabled:opacity-50"
                >
                  <XCircle className="w-3 h-3" /> Reject
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DuplicatesView() {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [saving, setSaving] = useState(false);

  const loadClusters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ingestionService.getDuplicateClusters(null, statusFilter || undefined);
      const arr = Array.isArray(data?.clusters) ? data.clusters : Array.isArray(data) ? data : [];
      setClusters(arr);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadClusters(); }, [loadClusters]);

  const handleReview = async (clusterId, payload) => {
    await ingestionService.reviewCluster(clusterId, payload);
    loadClusters();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await ingestionService.saveDuplicates(null, 0.85);
      loadClusters();
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = clusters.filter(c => !c.status || c.status === 'pending').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Duplicate Document Clusters</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {pendingCount > 0 ? (
              <span className="text-yellow-600 font-medium">{pendingCount} cluster{pendingCount !== 1 ? 's' : ''} awaiting governance review</span>
            ) : (
              'All clusters have been reviewed'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Detect & Save
          </button>
          <button onClick={loadClusters} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-md text-xs text-gray-600 hover:bg-gray-50">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected', ''].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${
              statusFilter === s
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-sm text-gray-500">Loading clusters…</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          Failed to load: {error}
          <button onClick={loadClusters} className="ml-3 underline">Retry</button>
        </div>
      ) : clusters.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Copy className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No duplicate clusters found{statusFilter ? ` with status "${statusFilter}"` : ''}.</p>
          <p className="text-xs mt-1">Click "Detect & Save" to scan for near-duplicates.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {clusters.map((cluster, i) => (
            <ClusterCard key={cluster.cluster_id || cluster.id || i} cluster={cluster} onReview={handleReview} />
          ))}
        </div>
      )}
    </div>
  );
}
